"use client";

import { c as _c } from "react/compiler-runtime";
import { Download, AlertCircle, ImageIcon, ImagePlus, CornerDownLeft } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, useEffect, useState } from "react";
import { Actions, Action } from "@/components/ai-elements/actions";
import { Image } from "@/components/ai-elements/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/image-zoom";
import { Skeleton } from "@/components/ui/skeleton";
import { getHeroSuggestionGroups, sampleSuggestions } from "@/lib/hero-suggestions";
import { downloadImage } from "@/lib/image-gen";
import { useI18n } from "@llmgateway/i18n/react";
import type { GalleryItem } from "@/lib/image-gen";
interface ImageGalleryProps {
  items: GalleryItem[];
  comparisonMode: boolean;
  onSuggestionClick?: (prompt: string) => void;
  onUseAsReference?: (image: {
    base64: string;
    mediaType: string;
  }) => void;
  onInsertPrompt?: (prompt: string) => void;
}
const GalleryImage = memo(t0 => {
  const $ = _c(21);
  const {
    base64,
    mediaType,
    modelName
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== t) {
    t1 = t("studio.generatedImage");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== base64 || $[3] !== mediaType || $[4] !== t1) {
    t2 = <ImageZoom className="h-full w-full"><Image base64={base64} mediaType={mediaType} alt={t1} className="!h-full !w-full !max-w-none object-cover !rounded-none" /></ImageZoom>;
    $[2] = base64;
    $[3] = mediaType;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("studio.downloadImage");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== base64 || $[9] !== mediaType) {
    t4 = () => downloadImage({
      base64,
      mediaType
    });
    $[8] = base64;
    $[9] = mediaType;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Download className="h-4 w-4" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4) {
    t6 = <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"><Button variant="secondary" size="icon" className="h-8 w-8 bg-background/80 backdrop-blur-sm" aria-label={t3} onClick={t4}>{t5}</Button></div>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== modelName) {
    t7 = modelName && <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity"><Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-xs">{modelName}</Badge></div>;
    $[15] = modelName;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="group relative h-64 overflow-hidden rounded-lg border">{t2}{t6}{t7}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
});
function LoadingSkeleton(t0) {
  const $ = _c(2);
  const {
    count
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = <div className="grid grid-cols-2 gap-3 max-w-lg">{Array.from({
        length: count
      }).map(_temp)}</div>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-64 rounded-lg" />;
}
function EmptyState(t0) {
  const $ = _c(21);
  const {
    onSuggestionClick
  } = t0;
  const {
    locale,
    t
  } = useI18n();
  const [suggestions, setSuggestions] = useState(null);
  let t1;
  let t2;
  if ($[0] !== locale) {
    t1 = () => {
      const suggestionGroups = getHeroSuggestionGroups(locale);
      setSuggestions(sampleSuggestions(suggestionGroups["Image gen"], 6));
    };
    t2 = [locale];
    $[0] = locale;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ImageIcon className="h-16 w-16 text-muted-foreground/30 mb-6" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("studio.noImagesYet");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <h3 className="text-lg font-medium mb-2">{t4}</h3>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("studio.imageEmptyDescription");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <p className="text-sm text-muted-foreground mb-8 max-w-md">{t6}</p>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onSuggestionClick || $[13] !== suggestions) {
    t8 = suggestions ? <motion.div key="suggestions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.07,
      ease: "easeOut"
    }} className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">{suggestions.map((s, index) => <motion.button key={s} type="button" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.12,
        delay: index * 0.025,
        ease: "easeOut"
      }} onClick={() => onSuggestionClick?.(s)} className="rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60 transition-colors">{s}</motion.button>)}</motion.div> : null;
    $[12] = onSuggestionClick;
    $[13] = suggestions;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <AnimatePresence>{t8}</AnimatePresence>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t7 || $[19] !== t9) {
    t10 = <div className="flex flex-col items-center justify-center py-20 text-center">{t3}{t5}{t7}{t9}</div>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function InputImageThumbnails(t0) {
  const $ = _c(7);
  const {
    images
  } = t0;
  const {
    t
  } = useI18n();
  if (!images || images.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== images || $[1] !== t) {
    let t2;
    if ($[3] !== t) {
      t2 = (img, i) => <img key={i} src={img.dataUrl} alt={t("studio.inputImage", {
        count: i + 1
      })} className="h-6 w-6 rounded border object-cover" />;
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = images.map(t2);
    $[0] = images;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex gap-1 shrink-0">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function SingleModeItem(t0) {
  const $ = _c(28);
  const {
    item,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  const {
    t
  } = useI18n();
  const model = item.models[0];
  if (!model) {
    return null;
  }
  const firstImage = model.images[0];
  let t1;
  if ($[0] !== item.inputImages) {
    t1 = <InputImageThumbnails images={item.inputImages} />;
    $[0] = item.inputImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.prompt) {
    t2 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[2] = item.prompt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.timestamp) {
    t3 = new Date(item.timestamp).toLocaleTimeString();
    $[4] = item.timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-xs text-muted-foreground shrink-0">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== model.error || $[13] !== model.imageCount || $[14] !== model.images || $[15] !== model.isLoading) {
    t6 = model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.images.length === 0 && model.isLoading ? <LoadingSkeleton count={Math.max(1, model.imageCount ?? 1)} /> : <div className={`grid gap-3 ${model.images.length === 1 && !model.isLoading ? "grid-cols-1 max-w-xs" : "grid-cols-2 max-w-lg"}`}>{model.images.map(_temp2)}{model.isLoading && <Skeleton className="h-64 rounded-lg" />}</div>;
    $[12] = model.error;
    $[13] = model.imageCount;
    $[14] = model.images;
    $[15] = model.isLoading;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== firstImage || $[18] !== item.prompt || $[19] !== model.isLoading || $[20] !== onInsertPrompt || $[21] !== onUseAsReference || $[22] !== t) {
    t7 = !model.isLoading && (firstImage || onInsertPrompt) && <Actions>{firstImage && onUseAsReference && <Action tooltip={t("studio.useAsImageReference")} onClick={() => onUseAsReference(firstImage)}><ImagePlus className="h-4 w-4" /></Action>}{onInsertPrompt && <Action tooltip={t("studio.insertPrompt")} onClick={() => onInsertPrompt(item.prompt)}><CornerDownLeft className="h-4 w-4" /></Action>}</Actions>;
    $[17] = firstImage;
    $[18] = item.prompt;
    $[19] = model.isLoading;
    $[20] = onInsertPrompt;
    $[21] = onUseAsReference;
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <div className="space-y-2">{t5}{t6}{t7}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}
function _temp2(img, idx) {
  return <GalleryImage key={idx} base64={img.base64} mediaType={img.mediaType} />;
}
function ComparisonModeItem(t0) {
  const $ = _c(28);
  const {
    item,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  const {
    t
  } = useI18n();
  const firstImage = item.models[0]?.images[0];
  let t1;
  if ($[0] !== item.inputImages) {
    t1 = <InputImageThumbnails images={item.inputImages} />;
    $[0] = item.inputImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.prompt) {
    t2 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[2] = item.prompt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.timestamp) {
    t3 = new Date(item.timestamp).toLocaleTimeString();
    $[4] = item.timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-xs text-muted-foreground shrink-0">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `grid gap-4 ${item.models.length === 1 ? "grid-cols-1" : item.models.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`;
  let t7;
  if ($[12] !== item.models) {
    t7 = item.models.map(_temp3);
    $[12] = item.models;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== firstImage || $[18] !== item.models || $[19] !== item.prompt || $[20] !== onInsertPrompt || $[21] !== onUseAsReference || $[22] !== t) {
    t9 = !item.models.some(_temp4) && (firstImage || onInsertPrompt) && <Actions>{firstImage && onUseAsReference && <Action tooltip={t("studio.useAsImageReference")} onClick={() => onUseAsReference(firstImage)}><ImagePlus className="h-4 w-4" /></Action>}{onInsertPrompt && <Action tooltip={t("studio.insertPrompt")} onClick={() => onInsertPrompt(item.prompt)}><CornerDownLeft className="h-4 w-4" /></Action>}</Actions>;
    $[17] = firstImage;
    $[18] = item.models;
    $[19] = item.prompt;
    $[20] = onInsertPrompt;
    $[21] = onUseAsReference;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t5 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className="space-y-2">{t5}{t8}{t9}</div>;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function _temp4(m) {
  return m.isLoading;
}
function _temp3(model) {
  return <div key={model.modelId} className="space-y-2"><Badge variant="outline" className="text-xs">{model.modelName}</Badge>{model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.images.length === 0 && model.isLoading ? <LoadingSkeleton count={Math.max(1, model.imageCount ?? 1)} /> : <div className={`grid gap-2 ${model.images.length === 1 && !model.isLoading ? "grid-cols-1" : "grid-cols-2"}`}>{model.images.map((img, idx) => <GalleryImage key={idx} base64={img.base64} mediaType={img.mediaType} modelName={model.modelName} />)}{model.isLoading && <Skeleton className="h-64 rounded-lg" />}</div>}</div>;
}
export function ImageGallery(t0) {
  const $ = _c(13);
  const {
    items,
    comparisonMode,
    onSuggestionClick,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== onSuggestionClick) {
      t1 = <EmptyState onSuggestionClick={onSuggestionClick} />;
      $[0] = onSuggestionClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== comparisonMode || $[3] !== items || $[4] !== onInsertPrompt || $[5] !== onUseAsReference) {
    let t2;
    if ($[7] !== comparisonMode || $[8] !== onInsertPrompt || $[9] !== onUseAsReference) {
      t2 = item => <div key={item.id} id={`gallery-${item.id}`}>{comparisonMode ? <ComparisonModeItem item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} /> : <SingleModeItem item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} />}</div>;
      $[7] = comparisonMode;
      $[8] = onInsertPrompt;
      $[9] = onUseAsReference;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = items.map(t2);
    $[2] = comparisonMode;
    $[3] = items;
    $[4] = onInsertPrompt;
    $[5] = onUseAsReference;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <div className="space-y-8">{t1}</div>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}